import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { handleVAST } from '../vast-parser';
import HLSSource from './hls/HLSSource';
import * as browser from '../utils/browser';
import { getErrorSchema, isHls, isVideoChild, mediaProperties, startTimer, throttle } from '../utils';
import AdControllers from './AdControllers';

const propTypes = {
  actions: PropTypes.object,
  player: PropTypes.object,
  children: PropTypes.any,
  startTime: PropTypes.number,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  forceAutoPlay: PropTypes.bool,
  targetTotalTime: PropTypes.number,
  playsInline: PropTypes.bool,
  src: PropTypes.string,
  poster: PropTypes.string,
  className: PropTypes.string,
  preload: PropTypes.oneOf(['auto', 'metadata', 'none']),
  crossOrigin: PropTypes.string,
  onLoadStart: PropTypes.func,
  onWaiting: PropTypes.func,
  onCanPlay: PropTypes.func,
  onCanPlayThrough: PropTypes.func,
  onPlaying: PropTypes.func,
  onEnded: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onProgress: PropTypes.func,
  onDurationChange: PropTypes.func,
  onError: PropTypes.func,
  onSuspend: PropTypes.func,
  onAbort: PropTypes.func,
  onEmptied: PropTypes.func,
  onStalled: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onSecondsPlayedReached: PropTypes.func,
  onLoadedData: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onRateChange: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onAllAdsCompleted: PropTypes.func,
  sendPlayerEvent: PropTypes.func,
  onAdError: PropTypes.func,
  onAdStart: PropTypes.func,
  debugMode: PropTypes.bool,
  onAdPlaying: PropTypes.func,
  onStartFirstTime: PropTypes.func,
  onAdStartFirstTime: PropTypes.func,
  onVASTLoadedFirstTime: PropTypes.func,
  onFirstPlayAttempt: PropTypes.func,
  onFirstPlayAdAttempt: PropTypes.func,
  onStartError: PropTypes.func,
  chapters: PropTypes.array,
  storyboard: PropTypes.object,
};

const defaultProps = {
  preload: 'auto',
};

export default class Video extends Component {
  constructor(props) {
    super(props);
    this.isFirstTimeToPlay = true;
    this.video = null;
    this.isVideoPlayed = false;
    this.isReachedSecond = false;
    this.delayToStartVideoTimer = null;
    this.delayToStartVideoAdTimer = null;
    this.videoWaitingTimer = null;
    this.defaultAdState = {
      skipTimeOffset: null,
      skipOn: null,
      time: 0,
      clickThroughUrl: null,
      isPlaying: false,
      isPlayed: false,
      isCompleted: false,
      source: null,
      name: null
    };
    this.totalWaitingDuration = 0;
    this.waitingInterval = null;
    this.inlinePlay = browser.IS_IOS;
    this.state = {
      ad: this.defaultAdState,
      allowToPlay: false,
      allowToAutoPlay: true,
      videoURL: null,
      currentError: null
    };
    this.metricIntervalTimeout = 10000;
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.seek = this.seek.bind(this);
    this.forward = this.forward.bind(this);
    this.replay = this.replay.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.getProperties = this.getProperties.bind(this);
    this.handleLoadStart = this.handleLoadStart.bind(this);
    this.handleCanPlay = this.handleCanPlay.bind(this);
    this.handleCanPlayThrough = this.handleCanPlayThrough.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handleWaiting = this.handleWaiting.bind(this);
    this.handleSeeking = this.handleSeeking.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleSeeked = this.handleSeeked.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleSuspend = this.handleSuspend.bind(this);
    this.handleAbort = this.handleAbort.bind(this);
    this.handleEmptied = this.handleEmptied.bind(this);
    this.handleStalled = this.handleStalled.bind(this);
    this.handleLoadedMetaData = this.handleLoadedMetaData.bind(this);
    this.handleLoadedData = this.handleLoadedData.bind(this);
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleRateChange = this.handleRateChange.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.handleDurationChange = this.handleDurationChange.bind(this);
    this.handleProgress = throttle(this.handleProgress.bind(this), 250);
    this.adHandleElapsedTime = this.adHandleElapsedTime.bind(this);
  }

  async componentDidMount() {
    this.methodCalls('componentDidMount');
    window.addEventListener('resize', this.handleResize);
    await this.handleVideoContentInit();
    if (!this.autoPlay) {
      this.setContentLoading(false);
    }
  }

  componentWillMount() {
    this.methodCalls('componentWillMount');
  }

  componentWillUnmount() {
    this.methodCalls('componentWillUnmount');
    window.removeEventListener('resize', this.handleResize);

    if (this.delayToStartVideoTimer) {
      this.delayToStartVideoTimer.end();
    }
    if (this.delayToStartVideoAdTimer) {
      this.delayToStartVideoAdTimer.end();
    }
    if (this.videoWaitingTimer) {
      this.videoWaitingTimer.end();
    }

    this.delayToStartVideoTimer = null;
    this.delayToStartVideoAdTimer = null;
    this.videoWaitingTimer = null;

    this.isVideoPlayed = false;
    this.isReachedSecond = false;
    this.totalWaitingDuration = 0;
    if (this.waitingInterval) {
      clearInterval(this.waitingInterval);
      this.waitingInterval = null;
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.src !== this.props.src) {
      this.video = null;
      this.setState({
        videoURL: nextProps.src,
      }, () => {
        this.play();
      });
    }
  }

  get isSafe() {
    return this.state.videoURL && !!this.video;
  }

  get autoPlay() {
    return this.state.allowToAutoPlay && this.props.autoPlay;
  }

  getVideoSRC() {
    let videoSRC;
    if (this.props.src) {
      videoSRC = this.props.src;
    } else if (this.props.children) {
      React.Children.toArray(this.props.children)
        .filter(isVideoChild)
        .map(component => {
          let componentProps;
          if (typeof component.type === 'string' && component.type === 'source') {
            componentProps = { ...component.props };
          }
          videoSRC = componentProps.src;
        });
    }
    return videoSRC || null;
  }

  setVideoURL(videoURL = this.getVideoSRC()) {
    this.methodCalls('setVideoURL');
    if (videoURL !== this.state.videoURL) {
      this.methodCalls('setVideoURL', videoURL);
      this.setState({
        videoURL
      });
    }
  }

  // get all video properties
  getProperties() {
    if (!this.video) {
      return null;
    }

    return mediaProperties.reduce((properties, key) => {
      properties[key] = this.video[key];
      return properties;
    }, {});
  }

  // get playback rate
  get playbackRate() {
    this.methodCalls('playbackRate');
    if (this.isSafe) {
      return this.video.playbackRate;
    }
  }

  set currentTime(time) {
    this.methodCalls('currentTime', time);
    if (this.isSafe) {
      this.video.currentTime = time;
    }
  }

  set playbackRate(rate) {
    this.methodCalls('playbackRate', rate);
    if (this.isSafe) {
      this.video.playbackRate = rate;
    }
  }

  get muted() {
    this.methodCalls('get muted', this.video.muted);
    if (this.isSafe) {
      return this.video.muted;
    }
  }

  set muted(val) {
    this.methodCalls('set muted', val);
    if (this.isSafe) {
      this.video.muted = val;
    }
  }

  get volume() {
    this.methodCalls('get volume', this.video.volume);
    if (this.isSafe) {
      return this.video.volume;
    }
  }

  set volume(val) {
    if (val > 1) {
      val = 1;
    }
    if (val < 0) {
      val = 0;
    }
    this.methodCalls('set volume', val);
    if (this.isSafe) {
      this.video.volume = val;
    }
  }

  // video width
  get videoWidth() {
    this.methodCalls('get videoWidth', this.video.videoWidth);
    if (this.isSafe) {
      return this.video.videoWidth;
    }
  }

  // video height
  get videoHeight() {
    this.methodCalls('get videoHeight', this.video.videoHeight);
    if (this.isSafe) {
      return this.video.videoHeight;
    }
  }

  setContentLoading(contentLoading = true) {
    const { actions } = this.props;
    if (this.isVideoPlayed === contentLoading) {
      this.isVideoPlayed = !contentLoading;
      actions.handleContentLoading(contentLoading);
    }
  }

  set allowToPlay(allowToPlay) {
    this.methodCalls('set allowToPlay', allowToPlay);
    if (allowToPlay !== this.state.allowToPlay) {
      this.setState({
        allowToPlay
      });
    }
  }

  set allowToAutoPlay(allowToAutoPlay) {
    this.methodCalls('set allowToAutoPlay', allowToAutoPlay);
    if (allowToAutoPlay !== this.state.allowToAutoPlay) {
      this.setState({
        allowToAutoPlay
      });
    }
  }

  play(attempts = 0, forceToPlay = false) {
    if (this.isSafe) {
      this.setContentLoading(false);
      const { forceAutoPlay } = this.props,
        { allowToPlay, ad } = this.state;
      if (allowToPlay || forceToPlay) {
        // const { muted } = this.props;
        this.methodCalls('play', attempts);
        const promise = this.video.play();
        if (promise) {
          promise
            .then(() => {
              this.adPlayEvent();
              const { onFirstPlayAttempt, onFirstPlayAdAttempt } = this.props;
              if (this.isFirstTimeToPlay) {
                this.isFirstTimeToPlay = false;
                if (!ad.isPlaying && onFirstPlayAttempt) {
                  onFirstPlayAttempt();
                }
                if (ad.isPlaying && onFirstPlayAdAttempt) {
                  onFirstPlayAdAttempt();
                }
              }
            })
            .catch(error => {
              const isNotAllowedError = error && error.name === 'NotAllowedError';
              this.error(error);
              if (forceAutoPlay) {
                if (isNotAllowedError) {
                  this.muted = true;
                }
                if (attempts < 10) {
                  setTimeout(() => this.play(attempts + 1), 200);
                }
              } else if (isNotAllowedError) {
                this.allowToAutoPlay = false;
              }
            });
          return promise;
        }
      }
    }
  }

  // pause the video
  pause(sendEvent = true) {
    this.methodCalls('pause');
    if (sendEvent) {
      this.adPausedEvent();
    }
    if (this.isSafe) {
      this.video.pause();
    }
  }

  // Change the video source and re-load the video:
  load() {
    this.methodCalls('load');
    if (this.isSafe) {
      this.video.load();
    }
  }

  // Add a new text track to the video
  addTextTrack(...args) {
    this.methodCalls('addTextTrack');
    if (this.isSafe) {
      this.video.addTextTrack(...args);
    }
  }

  // Check if your browser can play different types of video:
  canPlayType(...args) {
    this.methodCalls('canPlayType');
    if (this.isSafe) {
      this.video.canPlayType(...args);
    }
  }

  // toggle play
  togglePlay() {
    this.methodCalls('togglePlay');
    if (this.isSafe) {
      if (this.video.paused) {
        this.play();
      } else {
        this.pause();
      }
    }
  }

  // seek video by time
  seek(time) {
    this.methodCalls('time');
    if (this.isSafe) {
      try {
        this.currentTime = time;
      } catch (e) {
        this.error('seek', e);
      }
    }
  }

  // jump forward x seconds
  forward(seconds) {
    this.methodCalls('forward', seconds);
    if (this.isSafe) {
      this.seek(this.video.currentTime + seconds);
    }
  }

  // jump back x seconds
  replay(seconds) {
    this.methodCalls('replay', seconds);
    if (this.isSafe) {
      this.forward(-seconds);
    }
  }

  // enter or exist full screen
  toggleFullscreen() {
    this.methodCalls('toggleFullscreen');
    const { player, actions } = this.props;
    actions.toggleFullscreen(player);
  }

  // Fired when the user agent
  // begins looking for media data
  handleLoadStart(...args) {
    this.methodCalls('handleLoadStart');
    const { actions, onLoadStart } = this.props;
    actions.handleLoadStart(this.getProperties());

    if (!this.delayToStartVideoTimer || !this.delayToStartVideoAdTimer) {
      this.delayToStartVideoAdTimer = startTimer();
      this.delayToStartVideoTimer = startTimer();
    }
    if (onLoadStart) {
      onLoadStart(...args);
    }
  }

  // A handler for events that
  // signal that waiting has ended
  handleCanPlay(...args) {
    this.methodCalls('handleCanPlay');
    const { actions, onCanPlay } = this.props;

    actions.handleCanPlay(this.getProperties());

    if (onCanPlay) {
      onCanPlay(...args);
    }
  }

  // A handler for events that
  // signal that waiting has ended
  handleCanPlayThrough(...args) {
    this.methodCalls('handleCanPlayThrough');
    const { actions, onCanPlayThrough } = this.props;

    // Calculate exact delay to start playing
    if (this.delayToStartVideoTimer && this.isFirstTimeToPlay) {
      this.handleDelayToStartPlaying();
      this.handleDelayToAdStartPlaying();
    } else {
      if (this.state.currentError) {
        if (this.state.currentError.isFatal) {
          this.setWaitingFlag();
        }
        this.setState({
          currentError: null
        });
      }
      this.handleWaitingDuration();
    }

    actions.handleCanPlayThrough(this.getProperties());

    if (onCanPlayThrough) {
      onCanPlayThrough(...args);
    }
  }

  handleDelayToStartPlaying() {
    const { onStartFirstTime } = this.props,
      { ad } = this.state;

    if (!ad.isPlaying && onStartFirstTime) {
      onStartFirstTime({
        quality: this.trackQuality,
        delayToStartPlaying: this.delayToStartVideoTimer.end() / 1000,
      });
    }
  }

  handleDelayToAdStartPlaying() {
    const { onAdStartFirstTime } = this.props,
      { ad } = this.state;

    if (ad.isPlaying && onAdStartFirstTime) {
      onAdStartFirstTime({
        delayToStartPlaying: this.delayToStartVideoAdTimer.end() / 1000,
      });
    }
  }

  handleWaitingDuration(removeInterval = true) {
    if (this.videoWaitingTimer) {
      if (removeInterval) {
        clearInterval(this.waitingInterval);
      }

      const { onWaitingStop } = this.props,
        currentWaitingDuration = this.videoWaitingTimer.end() / 1000;
      if (currentWaitingDuration > 0.1) {
        this.totalWaitingDuration += currentWaitingDuration;
        if (onWaitingStop) {
          onWaitingStop({
            quality: this.trackQuality,
            waitingDuration: currentWaitingDuration,
            totalWaitingDuration: this.totalWaitingDuration,
          });
        }
      }
      this.videoWaitingTimer = null;
    }
  }

  get trackQuality() {
    const { player: { tracks, realActiveTrack } } = this.props;
    return tracks && tracks.length > 1 && tracks.filter(item => item.id === realActiveTrack)[0].label || null;
  }

  // A handler for events that
  // signal that waiting has ended
  handlePlaying(...args) {
    this.methodCalls('handlePlaying');
    const { actions, onPlaying } = this.props;
    actions.handlePlaying(this.getProperties());

    if (onPlaying) {
      onPlaying(...args);
    }
  }

  // Fired whenever the media has been started
  handlePlay(...args) {
    this.methodCalls('handlePlay');
    const { actions, onPlay } = this.props;
    actions.handlePlay(this.getProperties());
    if (onPlay) {
      onPlay(...args);
    }
  }

  // Fired whenever the media has been paused
  handlePause(...args) {
    this.methodCalls('handlePause');
    const { actions, onPause } = this.props;
    actions.handlePause(this.getProperties());

    if (onPause) {
      onPause(...args);
    }
  }

  // Fired when the duration of
  // the media resource is first known or changed
  handleDurationChange(...args) {
    this.methodCalls('handleDurationChange');
    const { actions, onDurationChange } = this.props;
    actions.handleDurationChange(this.getProperties());

    if (onDurationChange) {
      onDurationChange(...args);
    }
  }

  // Fired while the user agent
  // is downloading media data
  handleProgress(...args) {
    this.methodCalls('handleProgress');
    const { actions, onProgress } = this.props;
    if (this.video) {
      actions.handleProgressChange(this.getProperties());
    }

    if (onProgress) {
      onProgress(...args);
    }
  }

  // Fired when the end of the media resource
  // is reached (currentTime == duration)
  handleEnded(...args) {
    this.methodCalls('handleEnded');
    const { loop, player, actions, onEnded } = this.props,
      { ad: { source } } = this.state;
    if (source) {
      this.adHandleEnd();
    } else {
      if (loop) {
        this.seek(0);
        this.play();
      } else if (!player.paused) {
        this.pause();
      }
      actions.handleEnd(this.getProperties());

      if (onEnded) {
        onEnded(...args);
      }
    }
  }

  // Fired whenever the player
  // is jumping to a new time
  handleSeeking(...args) {
    this.methodCalls('handleSeeking');

    if (this.video.paused) {
      this.setWaitingFlag();
    }

    const { actions, onSeeking } = this.props;
    actions.handleSeeking(this.getProperties());

    if (onSeeking) {
      onSeeking(...args);
    }
  }

  // Fired when the player has
  // finished jumping to a new time
  handleSeeked(...args) {
    this.methodCalls('handleSeeked');
    const { actions, onSeeked } = this.props;
    actions.handleSeeked(this.getProperties());

    if (onSeeked) {
      onSeeked(...args);
    }
  }

  // Fires when the browser is
  // intentionally not getting media data
  handleSuspend(...args) {
    this.methodCalls('handleSuspend');
    const { actions, onSuspend } = this.props;
    actions.handleSuspend(this.getProperties());
    if (onSuspend) {
      onSuspend(...args);
    }
  }

  // Fires when the loading of an audio/video is aborted
  handleAbort(...args) {
    this.methodCalls('handleAbort');

    const { actions, onAbort } = this.props;
    actions.handleAbort(this.getProperties());

    if (onAbort) {
      onAbort(...args);
    }
  }

  // Fires when the current playlist is empty
  handleEmptied(...args) {
    this.methodCalls('handleEmptied');

    const { actions, onEmptied } = this.props;
    actions.handleEmptied(this.getProperties());

    if (onEmptied) {
      onEmptied(...args);
    }
  }

  // Fires when the browser is trying to
  // get media data, but data is not available
  handleStalled(...args) {
    this.methodCalls('handleStalled');

    const { actions, onStalled } = this.props;
    actions.handleStalled(this.getProperties());

    if (onStalled) {
      onStalled(...args);
    }
  }

  // Fires when the browser has loaded
  // meta data for the audio/video
  handleLoadedMetaData(...args) {
    this.methodCalls('handleLoadedMetaData');
    if (this.isSafe) {
      const { actions, onLoadedMetadata, adsTagUrl } = this.props,
        { ad: { isCompleted } } = this.state;

      actions.handleLoadedMetaData(this.getProperties());

      if (this.inlinePlay && adsTagUrl && isCompleted) {
        this.play();
      } else {
        this.setContentLoading(false);
      }

      if (onLoadedMetadata) {
        onLoadedMetadata(...args);
      }
    }
  }

  // Fires when the browser has loaded
  // the current frame of the audio/video
  handleLoadedData(...args) {
    this.methodCalls('handleLoadedData');

    const { actions, onLoadedData, startTime } = this.props,
      { ad: { isPlaying } } = this.state;

    actions.handleLoadedData(this.getProperties());

    if (!isPlaying && startTime && startTime > 0) {
      this.currentTime = startTime;
    }

    if (onLoadedData) {
      onLoadedData(...args);
    }

    if (this.autoPlay) {
      this.play();
    } else {
      this.setContentLoading(false);
    }

  }

  // Fired whenever the media begins waiting
  handleWaiting(...args) {
    this.methodCalls('handleWaiting', this.props.player);
    const { actions, onWaiting } = this.props;
    this.setWaitingFlag();
    actions.handleWaiting(this.getProperties());
    if (onWaiting) {
      onWaiting(...args);
    }
  }

  setWaitingFlag() {
    const { player: { currentTime } } = this.props;

    if (!this.videoWaitingTimer && currentTime) {
      this.videoWaitingTimer = startTimer();

      this.waitingInterval = setInterval(() => {
        this.handleWaitingDuration(false);
        this.handleTimeUpdate();
        if (this.videoWaitingTimer) {
          this.videoWaitingTimer.end();
        }
        this.videoWaitingTimer = startTimer();
      }, this.metricIntervalTimeout);

    }
  }

  // Fires when the current
  // playback position has changed
  handleTimeUpdate(...args) {
    // reset waiting flag until current time if it set.
    if (this.isSafe) {
      if (this.videoWaitingTimer) {
        this.handleWaitingDuration();
        this.setWaitingFlag();
      }

      const { actions, onTimeUpdate, onSecondsPlayedReached, targetTotalTime = 10, player: { currentTime, duration, hls, isLive } } = this.props,
        { ad } = this.state,
        timeRanges = this.video.played;

      let secondsPlayed = 0,
        percentPlayed = 0;

      if (duration && timeRanges) {
        for (let i = 0; i < timeRanges.length; i++) {
          secondsPlayed = secondsPlayed + (timeRanges.end(i) - timeRanges.start(i));
        }
        percentPlayed = (100 / duration) * secondsPlayed;
      }

      actions.handleTimeUpdate(this.getProperties(), secondsPlayed, percentPlayed, this.totalWaitingDuration);
      if (isLive && hls &&
        hls.levels[hls.currentLevel] &&
        hls.levels[hls.currentLevel].details) {
        const targetDuration = hls.levels[hls.currentLevel].details.targetduration,
          liveSync = hls.config.liveSyncDurationCount,
          liveOffset = targetDuration * liveSync,
          liveTime = duration - liveOffset,
          latency = liveTime - currentTime;
        actions.handleMediaLatencyChange(liveTime, latency);
      }

      if (!ad.isPlaying && onSecondsPlayedReached && secondsPlayed >= targetTotalTime && !this.isReachedSecond) {
        this.isReachedSecond = true;
        onSecondsPlayedReached(...args);
      }

      if (onTimeUpdate) {
        onTimeUpdate(...args);
      }
    }
  }

  /**
   * Fires when the playing speed of the audio/video is changed
   */
  handleRateChange(...args) {
    this.methodCalls('handleRateChange');

    const { actions, onRateChange } = this.props;
    actions.handleRateChange(this.getProperties());

    if (onRateChange) {
      onRateChange(...args);
    }
  }

  // Fires when the volume has been changed
  handleVolumeChange(...args) {
    this.methodCalls('handleVolumeChange');

    const { actions, onVolumeChange } = this.props;
    actions.handleVolumeChange(this.getProperties());

    if (onVolumeChange) {
      onVolumeChange(...args);
    }
  }

  // Fires when an error occurred
  // during the loading of an audio/video
  handleError(type, data) {
    const reason = getErrorSchema(data);
    this.error(type, data, reason);

    const { actions, onError } = this.props;
    actions.handleError(this.getProperties());

    if (onError) {
      onError(type, data, reason);
    }

    if (this.isFirstTimeToPlay) {
      this.handleStartupError(reason);
    }

    this.setState({
      currentError: reason
    });

    if (reason && reason.isFatal && this.videoWaitingTimer) {
      this.handleWaitingDuration();
    }
  }

  handleStartupError(reason) {
    this.methodCalls('handleStartupError', reason);
    const { onFirstPlayAttempt, onStartError } = this.props,
      { ad } = this.state;
    if (!ad.isPlaying && this.isFirstTimeToPlay && onFirstPlayAttempt) {
      this.isFirstTimeToPlay = false;
      onFirstPlayAttempt();
    }

    if (reason && reason.type && reason.detail && onStartError) {
      onStartError(reason, ad.isPlaying);
    }

    if (ad.isPlaying && (!reason || reason.isFatal)) {
      this.adEvent('error');
      this.destroyAd();
    }
  }

  handleResize(...args) {
    this.methodCalls('handleResize');

    const { actions, onResize } = this.props;
    actions.handleResize(this.getProperties());

    if (onResize) {
      onResize(...args);
    }
  }

  async handleVideoContentInit() {
    this.methodCalls('handleVideoContentInit');
    const { actions, adsTagUrl, onVASTLoadedFirstTime } = this.props;
    if (adsTagUrl) {
      try {
        const delayToLoadVastTimer = startTimer();
        this.log('getting VAST data');
        const VAST = await handleVAST(adsTagUrl);
        this.log('VAST data', VAST);
        if (VAST && VAST.length) {
          const { mediaFiles, videoClicks, skipOn, adTitle } = VAST[0];

          this.setState(({ ad }) => ({
            ad: {
              ...ad,
              source: mediaFiles[0].URL,
              clickThroughUrl: videoClicks || null,
              skipTimeOffset: skipOn ? skipOn + 1 : null,
              name: adTitle,
              isPlaying: true,
            },
            allowToPlay: true,
            videoURL: mediaFiles[0].URL
          }));

          this.adEvent('loaded');

          if (onVASTLoadedFirstTime) {
            onVASTLoadedFirstTime({
              delayToLoad: delayToLoadVastTimer.end() / 1000
            });
          }

          if (this.video) {
            this.video.addEventListener('timeupdate', this.adHandleElapsedTime);
          }
          actions.handleAdStart();
        }
      } catch (error) {
        // this.handleStartupError({
        //   type: 'VAST_ERROR',
        //   detail: error
        // });
        this.setVideoURL();
        this.error(error);
        this.adEvent('error');
        this.destroyAd();
        this.allowToPlay = true;
      }
    } else {
      this.setVideoURL();
      this.allowToPlay = true;
    }
  }

  get playerSource() {
    if (!this.isSafe) {
      return null;
    }

    const props = {
      ...this.props,
      video: this.video,
    };

    const { ad: { source }, videoURL } = this.state;

    this.video.controls = this.video && browser.IS_MOBILE && !source;
    this.video.playsInline = this.video && browser.IS_MOBILE;

    return React.Children.toArray(this.props.children)
        .filter(isVideoChild)
        .map(() => isHls(videoURL) && !browser.IS_IOS ? <HLSSource
            key={2}
            {...props}
            onError={this.handleError}
            src={videoURL}
        /> : <source key={1} src={videoURL}/>);
  }

  methodCalls(...logs) {
    this.log('Method: ', ...logs);
  }

  error(...logs) {
    if (this.props.debugMode) {
      console.error('VIDEO_REACT', ...logs);
    }
  }

  log(...logs) {
    if (this.props.debugMode) {
      console.log('VIDEO_REACT', ...logs);
    }
  }

  /*
  * ////////////////////////////// ADS METHODS //////////////////////////////
   */

  adEvent(event) {
    const { sendPlayerEvent } = this.props,
      { ad: { name } } = this.state;
    if (sendPlayerEvent) {
      sendPlayerEvent('Ads', event, name);
    }
  }

  adPlayEvent() {
    const { ad: { source, isPlaying } } = this.state;
    if (source) {
      this.adEvent(isPlaying ? 'play' : 'resume');
      this.setState(({ ad }) => ({
        ad: {
          ...ad,
          isPlaying: true,
        }
      }));
      this.isAdPlaying(true);
    }
  }

  adPausedEvent() {
    const { ad: { source } } = this.state;
    if (source) {
      this.adEvent('paused');
      this.setState(({ ad }) => ({
        ad: {
          ...ad,
          isPlaying: false
        }
      }));
    }
  }

  adEndEvent() {
    this.adEvent('complete');
  }

  adClickEvent() {
    this.adEvent('clicked');
  }

  adSkip() {
    this.adEvent('skip');
    this.adHandleEnd(false);
  }

  adMoreInfo() {
    this.pause(false);
    this.adClickEvent();
  }

  destroyAd() {
    this.methodCalls('destroyAd');
    if (this.delayToStartVideoTimer) {
      this.delayToStartVideoTimer.end();
    }
    this.delayToStartVideoTimer = startTimer();
    this.isFirstTimeToPlay = true;

    this.pause();

    const { actions } = this.props;
    actions.handleAdEnded();
    this.setState(({ ad }) => ({
      ad: {
        ...ad,
        ...this.defaultAdState,
        isPlayed: false,
        isPlaying: false,
        isCompleted: true,
      },
      allowToAutoPlay: true,
      allowToPlay: true,
    }));
    this.isAdPlaying(false);
    this.setContentLoading(true);

    if (this.video) {
      this.video.removeEventListener('timeupdate', this.adHandleElapsedTime);
      this.video = null;
    }
    this.setVideoURL();
  }

  adHandleEnd(sendEvent = true) {
    this.methodCalls('handleAdEnded');
    if (sendEvent) {
      this.adEndEvent();
    }
    this.destroyAd();
  }

  adHandleElapsedTime() {
    this.methodCalls('adHandleElapsedTime');
    if (this.isSafe) {
      const duration = Math.ceil(this.video.duration),
        currentTime = Math.ceil(this.video.currentTime),
        time = Math.abs(currentTime - duration);

      if (time !== this.state.ad.time) {
        const states = {
          time,
        };
        if (this.state.ad.skipTimeOffset) {
          states.skipOn = this.state.ad.skipTimeOffset - currentTime;
        }
        this.setState(({ ad }) => ({
          ad: {
            ...ad,
            ...states
          }
        }));
      }
    }
  }

  isAdPlaying(isAdPlaying) {
    this.methodCalls('isAdPlaying', isAdPlaying);
    const { onAdPlaying } = this.props,
      { ad: { isPlayed } } = this.state;

    if (isAdPlaying !== isPlayed) {
      if (onAdPlaying) {
        onAdPlaying(isAdPlaying);
      }

      if (isAdPlaying !== this.state.ad.isPlayed) {
        this.setState(({ ad }) => ({
          ad: {
            ...ad,
            isPlayed: isAdPlaying
          }
        }));
      }
    }
  }

  render() {
    return <div className="react-video-holder">
      {this.state.ad && this.state.ad.isPlayed &&
      <AdControllers onSkip={() => this.adSkip()} onLinkClick={() => this.adMoreInfo()} duration={this.state.ad.time} skipOn={this.state.ad.skipOn} URL={this.state.ad.clickThroughUrl}/>
      }
      <video
        className={classNames('video-react-video', this.props.className)}
        id={this.props.videoId}
        crossOrigin={this.props.crossOrigin}
        ref={(c) => {
          this.video = c;
        }}
        muted={this.props.muted}
        preload={this.props.preload}
        loop={this.props.loop}
        autoPlay={this.state.allowToPlay && this.autoPlay}
        poster={this.props.poster}
        src={this.state.videoURL}
        onLoadStart={this.handleLoadStart}
        onWaiting={this.handleWaiting}
        onCanPlay={this.handleCanPlay}
        onCanPlayThrough={this.handleCanPlayThrough}
        onPlaying={this.handlePlaying}
        onEnded={this.handleEnded}
        onSeeking={this.handleSeeking}
        onSeeked={this.handleSeeked}
        onPlay={this.handlePlay}
        onPause={this.handlePause}
        onProgress={this.handleProgress}
        onDurationChange={this.handleDurationChange}
        onError={this.handleError}
        onSuspend={this.handleSuspend}
        onAbort={this.handleAbort}
        onEmptied={this.handleEmptied}
        onStalled={this.handleStalled}
        onLoadedMetadata={this.handleLoadedMetaData}
        onLoadedData={this.handleLoadedData}
        onTimeUpdate={this.handleTimeUpdate}
        onRateChange={this.handleRateChange}
        onVolumeChange={this.handleVolumeChange}
      >
        {this.playerSource}
      </video>
    </div>;
  }
}

Video.propTypes = propTypes;
Video.defaultProps = defaultProps;
Video.displayName = 'Video';
